import { useAuth0, withAuthenticationRequired } from '@auth0/auth0-react';
import React, { useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { usePermissions } from '../../../hooks/usePermissions';
import { permissionRequiredFor } from '../../../utils/permissions';
import { PageButton } from '../../atoms/PageButton';
import { Wordmark } from '../../molecules/brand/Wordmark';
import { LoginButton } from '../../molecules/GdataLayout/LoginButton';
import { ThemeToggle } from '../../molecules/GdataLayout/ThemeToggle';
import { WorklistSurface } from '../../atoms/WorklistSurface';

/** Only a path of this application: `from` comes from the address bar. */
function safeFrom(from: string | null): string {
  return from && from.startsWith('/') && !from.startsWith('//') ? from : '/';
}

/**
 * 403: where a signed-in user lands when their account has no role yet, or lacks the permission a page needs. It
 * does not say which: the API's refusals are logged with their reason (viewer-api's `event=access_denied`).
 * "Check again" asks Auth0 for a new token, so a role given meanwhile lets them through without signing out; once
 * it does, they are sent on to what they asked for.
 */
export function ForbiddenPage() {
  const { user } = useAuth0();
  const { isLoading, roles, has, refresh } = usePermissions();
  const [params] = useSearchParams();
  const [checked, setChecked] = useState(false);
  const from = safeFrom(params.get('from'));
  const need = permissionRequiredFor(from.split(/[?#]/)[0]);

  const allowed = roles.length > 0 && (!need || has(need));
  if (!isLoading && allowed) {
    return <Navigate to={from} replace />;
  }

  const checkAgain = async () => {
    await refresh();
    setChecked(true);
  };

  return (
    <WorklistSurface>
      <header className="bg-dh-plum text-white shadow-sm">
        <div className="flex min-h-[64px] items-center gap-6 px-6">
          <Wordmark className="text-[24px] leading-none" />
          <div className="ml-auto flex items-center gap-1.5">
            <ThemeToggle />
            <LoginButton />
          </div>
        </div>
      </header>
      <main className="mx-auto mt-16 max-w-xl rounded-2xl border border-dl-border bg-dl-surface p-8">
        <p className="text-5xl font-semibold tabular-nums text-dl-accent">
          403
        </p>
        <h1 className="mb-4 mt-2 text-2xl font-semibold">Access forbidden</h1>
        {roles.length === 0 ? (
          <>
            <p className="mb-2">
              You are signed in
              {user?.email && (
                <>
                  {' '}
                  as <strong>{user.email}</strong>
                </>
              )}
              , but your account has no role on deeplabeling yet.
            </p>
            <p className="mb-6">
              Ask a data manager to give it one, then check again.
            </p>
          </>
        ) : (
          <p className="mb-6">
            Your account cannot open this page. If you need it, ask a data
            manager.
          </p>
        )}
        {checked && !isLoading && (
          <p role="status" className="mb-4 text-sm text-dl-muted">
            Still no access.
          </p>
        )}
        <div className="flex flex-wrap items-center gap-3">
          <PageButton
            tone="primary"
            className="mx-0"
            onClick={checkAgain}
            disabled={isLoading}
          >
            {isLoading ? 'Checking…' : 'Check again'}
          </PageButton>
          {roles.length > 0 && (
            <Link className="text-dl-accent underline" to="/">
              Back to the home page
            </Link>
          )}
        </div>
      </main>
    </WorklistSurface>
  );
}

export default withAuthenticationRequired(ForbiddenPage);
